import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      // Check if user has dismissed the prompt before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

      // Show prompt if never dismissed or dismissed more than 7 days ago
      if (!dismissed || Date.now() - dismissedTime > sevenDaysInMs) {
        // Wait 10 seconds before showing the prompt (let user explore the app first)
        setTimeout(() => setShowPrompt(true), 10000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setShowPrompt(false);
    } else {
      console.log('User dismissed the install prompt');
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
      setShowPrompt(false);
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  // Don't show if already installed or prompt not ready
  if (isInstalled || (!showPrompt && !isIOS)) {
    return null;
  }

  // iOS-specific instructions
  if (isIOS && showPrompt) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: '90%',
          width: '400px',
          backgroundColor: 'white',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          padding: 'var(--spacing-4)',
          zIndex: 9999,
          border: '1px solid var(--color-gray-300)',
        }}
      >
        <button
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: 'var(--spacing-2)',
            right: 'var(--spacing-2)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-gray-500)',
          }}
        >
          <X size={20} />
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-3)' }}>
          <div
            style={{
              backgroundColor: 'var(--color-primary-100)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-2)',
              flexShrink: 0,
            }}
          >
            <Smartphone size={24} color="var(--color-primary-600)" />
          </div>

          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'bold', marginBottom: 'var(--spacing-2)' }}>
              Install VERA App
            </h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-3)' }}>
              Install this app on your iPhone for quick access and offline capabilities.
            </p>
            <ol
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-gray-700)',
                paddingLeft: 'var(--spacing-4)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              <li>Tap the Share button (square with arrow)</li>
              <li>Scroll down and tap "Add to Home Screen"</li>
              <li>Tap "Add" in the top right corner</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Android/Chrome install prompt
  if (showPrompt && deferredPrompt) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: '90%',
          width: '400px',
          backgroundColor: 'white',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          padding: 'var(--spacing-4)',
          zIndex: 9999,
          border: '1px solid var(--color-gray-300)',
        }}
      >
        <button
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: 'var(--spacing-2)',
            right: 'var(--spacing-2)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-gray-500)',
          }}
        >
          <X size={20} />
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-3)' }}>
          <div
            style={{
              backgroundColor: 'var(--color-primary-100)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-2)',
              flexShrink: 0,
            }}
          >
            <Download size={24} color="var(--color-primary-600)" />
          </div>

          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'bold', marginBottom: 'var(--spacing-2)' }}>
              Install VERA App
            </h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-3)' }}>
              Install this app for quick access, offline capabilities, and a native app experience.
            </p>

            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <button
                onClick={handleDismiss}
                style={{
                  flex: 1,
                  padding: 'var(--spacing-2)',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'white',
                  color: 'var(--color-gray-700)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '500',
                }}
              >
                Not Now
              </button>
              <button
                onClick={handleInstallClick}
                style={{
                  flex: 1,
                  padding: 'var(--spacing-2)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--color-primary-600)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--spacing-2)',
                }}
              >
                <Download size={16} />
                Install
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPrompt;
